import aiChatService from '../services/aiChatService';
import processRawText from '../utils/messageParser';
import { findFixtureByUserText, findReplyChunks } from '../utils/testModeFixtures';

const TEST_MODE_CHUNK_DELAY_MS = 40;

const buildUserMessage = (text) => ({
  role: 'user',
  text,
  entityRefs: [],
  isStreaming: false,
});

const buildAiMessage = () => ({
  role: 'ai',
  rawText: '',
  reasoningSegments: [],
  text: '',
  suggestions: [],
  interactions: [],
  cards: [],
  entities: [],
  entityRefs: [],
  streamStatus: 'thinking',
  isReasoning: false,
  isStreaming: true,
  isWaiting: true,
});

/** Best-effort conversation_id when JSON.parse fails on SSE result (e.g. framing edge cases). */
const parseResultIdsFallback = (text) => {
  const mC = text.match(/"conversation_id"\s*:\s*"([0-9a-f-]{36})"/i);
  if (!mC?.[1]) return null;
  return { conversation_id: mC[1] };
};

const generateTopicId = () => (
  typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`
);

const buildPrimaryEntity = (entity) => {
  const label = entity.display_name || entity.name || entity.contact_name || entity.title || '';
  const base = { type: entity.type, uid: entity.uid, label };
  if (entity.type === 'matter') {
    const contactUid = entity.contact_uid || null;
    if (contactUid) base.contact_uid = contactUid;
  }
  if (entity.type === 'appointment') {
    const clientUid = entity.client_uid || entity.client_id || null;
    if (clientUid) base.client_uid = clientUid;
  }
  return base;
};

const buildTopicPayload = (entity) => {
  if (!entity?.type || !entity?.uid) return null;
  const primaryEntity = buildPrimaryEntity(entity);
  return {
    id: generateTopicId(),
    title: `${entity.type}: ${primaryEntity.label}`,
    trigger: 'entity_trigger',
    primary_entity: primaryEntity,
    created_at: new Date().toISOString(),
  };
};

const lastTopicLastMessage = (topics) => {
  const last = topics[topics.length - 1];
  return last ? last.messages[last.messages.length - 1] : null;
};

/** Immutably updates the last message of the last topic. */
const withLastMessage = (state, updater) => {
  const topics = [...state.topics];
  const lastTopic = { ...topics[topics.length - 1] };
  const messages = [...lastTopic.messages];
  messages[messages.length - 1] = updater({ ...messages[messages.length - 1] });
  lastTopic.messages = messages;
  topics[topics.length - 1] = lastTopic;
  return topics;
};

/** Same SSE handling for send and confirm (both POST …/message/stream). */
const handleChatStreamEvent = (commit) => (event) => {
  commit('markFirstEventReceived');
  if (event.type === 'error') {
    commit('setError', (event.text && event.text.trim()) || 'Something went wrong. Please try again.');
  }
  if (event.type === 'delta_reasoning') commit('appendReasoningDelta', event.text);
  if (event.type === 'tool_status' || event.type === 'status') commit('appendStatus', event.text);
  if (event.type === 'text_delta') commit('appendTextDelta', event.text);
  if (event.type === 'conversation_id') commit('setConversationId', event.text.trim());
  if (event.type === 'chat_topic') {
    try {
      const { title } = JSON.parse(event.text);
      if (title) commit('updateActiveTopicTitle', title);
    } catch { /* ignore malformed */ }
  }
  if (event.type === 'result') {
    try {
      commit('setResult', JSON.parse(event.text));
    } catch {
      const partial = parseResultIdsFallback(event.text);
      if (partial) commit('setResult', partial);
    }
  }
};

const state = {
  panelOpen: false,
  topics: [],
  conversationId: null,
  isStreaming: false,
  error: null,
  testMode: false,
  pendingTopic: null,
  activeTopic: null,
};

const getters = {
  panelOpen: (s) => s.panelOpen,
  topics: (s) => s.topics,
  isStreaming: (s) => s.isStreaming,
  error: (s) => s.error,
  testMode: (s) => s.testMode,
  activeTopic: (s) => s.activeTopic,
};

const mutations = {
  openPanel: (state) => { state.panelOpen = true; },
  closePanel: (state) => { state.panelOpen = false; },
  startNewTopic: (state, title) => {
    state.topics = [...state.topics, { title, messages: [] }];
  },
  setPendingTopic: (state, topic) => { state.pendingTopic = topic; },
  clearPendingTopic: (state) => { state.pendingTopic = null; },
  setActiveTopic: (state, topic) => { state.activeTopic = topic; },
  updateActiveTopicTitle: (state, title) => {
    if (state.activeTopic) state.activeTopic = { ...state.activeTopic, title };
    const topics = [...state.topics];
    const last = topics[topics.length - 1];
    if (last) topics[topics.length - 1] = { ...last, title };
    state.topics = topics;
  },
  appendMessage: (state, message) => {
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    lastTopic.messages = [...lastTopic.messages, message];
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setStreaming: (state, value) => { state.isStreaming = value; },
  setError: (state, message) => { state.error = message; },
  clearError: (state) => { state.error = null; },
  appendReasoningDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const segs = [...last.reasoningSegments];
      const lastSeg = segs[segs.length - 1];
      if (lastSeg && lastSeg.type === 'text') {
        segs[segs.length - 1] = { ...lastSeg, text: lastSeg.text + delta };
      } else {
        segs.push({ type: 'text', text: delta });
      }
      return { ...last, reasoningSegments: segs, isReasoning: true };
    });
  },
  appendStatus: (state, statusText) => {
    const trimmed = (statusText && String(statusText).trim()) || '';
    state.topics = withLastMessage(state, (last) => ({ ...last, streamStatus: trimmed || null }));
  },
  appendTextDelta: (state, delta) => {
    state.topics = withLastMessage(state, (last) => {
      const rawText = (last.isReasoning ? '' : last.rawText) + delta;
      const { text, afterText, suggestions, cards, entities, interactions, parts } = processRawText(rawText);
      return {
        ...last,
        rawText,
        text,
        afterText,
        suggestions,
        cards,
        entities,
        interactions,
        parts,
        isReasoning: false,
        streamStatus: null,
      };
    });
    // Sync activeTopic when BE emits a new_topic interaction inline in the stream
    const lastTopic = state.topics[state.topics.length - 1];
    const lastMsg = lastTopic?.messages[lastTopic.messages.length - 1];
    const beTopicTag = lastMsg?.interactions?.find((i) => i.type === 'new_topic');
    if (beTopicTag) {
      state.activeTopic = {
        id: beTopicTag.primary_entity_uid || `be-${Date.now()}`,
        title: beTopicTag.title || '',
        trigger: 'backend_detected',
        toolTip: beTopicTag.tool_tip || null,
        primary_entity: beTopicTag.primary_entity_type ? {
          type: beTopicTag.primary_entity_type,
          uid: beTopicTag.primary_entity_uid || null,
        } : null,
      };
    }
  },
  finalizeLastMessage: (state) => {
    state.topics = withLastMessage(state, (last) => ({
      ...last,
      isStreaming: false,
      isWaiting: false,
      streamStatus: null,
    }));
  },
  markFirstEventReceived: (state) => {
    if (!state.topics.length) return;
    const topics = [...state.topics];
    const lastTopic = { ...topics[topics.length - 1] };
    const messages = [...lastTopic.messages];
    const last = messages[messages.length - 1];
    if (!last || !last.isWaiting) return;
    messages[messages.length - 1] = { ...last, isWaiting: false };
    lastTopic.messages = messages;
    topics[topics.length - 1] = lastTopic;
    state.topics = topics;
  },
  setResult: (state, result) => {
    state.conversationId = result.conversation_id || state.conversationId;
    if (result.topic) {
      state.activeTopic = {
        ...(state.activeTopic || {}),
        ...result.topic,
        toolTip: result.topic.tool_tip || state.activeTopic?.toolTip || null,
      };
    }
  },
  setConversationId: (state, id) => {
    if (id) state.conversationId = id;
  },
  setTestMode: (state, value) => { state.testMode = !!value; },
  markConfirmActioned: (state, conversationId) => {
    state.topics = state.topics.map((topic) => ({
      ...topic,
      messages: topic.messages.map((msg) => (
        msg.role === 'confirm' && msg.conversationId === conversationId
          ? { ...msg, isActioned: true }
          : msg
      )),
    }));
  },
  resetConversation: (state) => {
    state.topics = [];
    state.conversationId = null;
    state.isStreaming = false;
    state.error = null;
    state.pendingTopic = null;
    state.activeTopic = null;
  },
};

/**
 * Replay canned text chunks through the normal stream handler, so the parser
 * and UI exercise exactly the same code path as a real SSE reply. Used only
 * when test-mode is active.
 *
 * We also `console.debug` every event we dispatch and the final parsed state,
 * so you can follow the "stream" and the post-parse Vuex state in DevTools.
 * Filter by `[AiChat test-mode]` in the Console to see the whole flow.
 */
const simulateStream = (commit, chunks, { label, getFinalMessage }) => new Promise((resolve) => {
  const onEvent = handleChatStreamEvent(commit);
  const tag = `[AiChat test-mode] ${label}`;
  const rawReply = chunks.join('');
  /* eslint-disable no-console */
  console.info(
    `%c${tag}%c raw LLM-like reply:\n%c${rawReply}`,
    'background:#4a90e2;color:#fff;padding:2px 6px;border-radius:3px;font-weight:bold',
    'color:inherit',
    'color:#888;white-space:pre-wrap',
  );
  console.groupCollapsed(`${tag} (per-chunk detail)`);
  console.debug(`${chunks.length} chunk(s), ${TEST_MODE_CHUNK_DELAY_MS}ms apart`);
  let i = 0;
  const next = () => {
    if (i >= chunks.length) {
      try {
        const finalMsg = getFinalMessage?.();
        if (finalMsg) {
          console.debug('final message state:', {
            text: finalMsg.text,
            entities: finalMsg.entities,
            interactions: finalMsg.interactions,
            suggestions: finalMsg.suggestions,
          });
        }
      } finally {
        console.groupEnd();
      }
      resolve();
      return;
    }
    const chunk = chunks[i];
    console.debug(`chunk ${i + 1}/${chunks.length} →`, chunk);
    onEvent({ type: 'text_delta', text: chunk });
    i += 1;
    setTimeout(next, TEST_MODE_CHUNK_DELAY_MS);
  };
  next();
  /* eslint-enable no-console */
});

const actions = {
  openPanel: ({ commit }) => commit('openPanel'),
  closePanel: ({ commit }) => commit('closePanel'),
  resetConversation: ({ commit }) => commit('resetConversation'),
  toggleTestMode: ({ commit, state: s }) => commit('setTestMode', !s.testMode),
  startNewTopic: ({ commit }, title) => commit('startNewTopic', title),

  /**
   * Called when the user clicks an entity card in the interaction list.
   * Idempotent: clicking the same entity that is already the active topic is a no-op.
   * Mints a new topic payload (sent on the next user message) and updates activeTopic.
   */
  addTopicEntity: ({ commit, state: s }, entity) => {
    if (s.activeTopic?.primary_entity?.uid === entity?.uid) return;
    commit('appendMessage', { role: 'topic-entity', entity, isStreaming: false });
    const topicPayload = buildTopicPayload(entity);
    if (topicPayload) {
      commit('setPendingTopic', topicPayload);
      commit('setActiveTopic', topicPayload);
    }
  },

  async sendMessage({ commit, state: s }, { text, context }) {
    commit('clearError');
    if (!s.topics.length) {
      commit('startNewTopic', context?.page || '');
    }
    commit('appendMessage', buildUserMessage(text));
    commit('appendMessage', buildAiMessage());
    commit('setStreaming', true);

    const fixture = s.testMode ? findFixtureByUserText(text) : null;
    if (fixture) {
      try {
        await simulateStream(commit, fixture.chunks, {
          label: `send "${text}"`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const payload = { message: text, user_view_context: context };
    if (s.conversationId) payload.conversation_id = s.conversationId;
    if (s.pendingTopic) {
      payload.chat_topic = {
        title: s.pendingTopic.title,
        entity: s.pendingTopic.primary_entity,
      };
      commit('clearPendingTopic');
    }

    try {
      await aiChatService.streamMessage(payload, handleChatStreamEvent(commit));
    } catch (err) {
      commit('setError', 'Something went wrong. Please try again.');
    } finally {
      commit('finalizeLastMessage');
      commit('setStreaming', false);
    }
  },

  /**
   * Reply to a HITL interaction streamed by the assistant.
   *
   * Accepts `{ uid?, confirm }`. Sends `{ interaction: { uid?, confirm }, conversation_id }`.
   * `uid` is present for code-driven (parked) slots; absent for LLM-driven confirms.
   * `confirm` is a string: "yes" executes, anything else is treated as a cancel reason.
   */
  async confirmAction({ commit, state: s }, payload) {
    const { uid, confirm, userText } = payload || {};
    commit('clearError');
    if (!s.conversationId) {
      commit('setError', 'Could not process your confirmation. Wait for the reply to finish, then try again.');
      return;
    }
    if (!confirm) {
      commit('setError', 'Could not process your confirmation. Please try again.');
      return;
    }
    if (userText) commit('appendMessage', buildUserMessage(userText));
    commit('appendMessage', buildAiMessage());
    commit('setStreaming', true);

    const replyChunks = s.testMode ? findReplyChunks(null, confirm) : null;
    if (replyChunks) {
      try {
        await simulateStream(commit, replyChunks, {
          label: `confirm → ${confirm}`,
          getFinalMessage: () => lastTopicLastMessage(s.topics),
        });
      } finally {
        commit('finalizeLastMessage');
        commit('setStreaming', false);
      }
      return;
    }

    const interaction = { confirm };
    if (uid) interaction.uid = uid;

    try {
      await aiChatService.streamMessage(
        { conversation_id: s.conversationId, interaction },
        handleChatStreamEvent(commit),
      );
    } catch (err) {
      commit('setError', 'Something went wrong. Please try again.');
    } finally {
      commit('finalizeLastMessage');
      commit('setStreaming', false);
    }
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
